import Analytics from '@ublend-npm/aula-analytics';
import {
  getAssessmentItemsBySpaceId,
  postAssignmentsToAssessmentItem,
  getGradesByAssessmentItemId,
  createExportJob,
  getGradesByStudentId,
} from './assessmentItems.effects';
import { getAssessmentItems } from './assessmentItems.selectors';
import { selectors as coreSelectors, actions as coreActions } from '$data/core';
import downloadGradeCsv from '$utils/downloadGradeCsv';
import mergeOverridesWithGrades from '$utils/mergeOverridesWithGrades';
import {
  EXPORT_GRADES_AS_CSV,
  FETCH_ASSESSMENT_ITEMS,
} from '$variables/analyticsEvents';

import {
  FETCH_ASSESSMENT_ITEMS_STARTED,
  FETCH_ASSESSMENT_ITEMS_SUCCESS,
  FETCH_ASSESSMENT_ITEMS_FAILED,
  MAP_ASSIGNMENTS_SUCCESS,
  MAP_ASSIGNMENTS_STARTED,
  MAP_ASSIGNMENTS_FAILED,
  MAP_ASSIGNMENTS_CLEAR,
  DOWNLOAD_CSV_STARTED,
  DOWNLOAD_CSV_SUCCESS,
  DOWNLOAD_CSV_FAILED,
  EXPORT_GRADES_STARTED,
  EXPORT_GRADES_SUCCESS,
  EXPORT_GRADES_FAILED,
  EXPORT_GRADES_EVENT_START,
  FETCH_AI_STUDENT_GRADES_STARTED,
  FETCH_AI_STUDENT_GRADES_SUCCESS,
  FETCH_AI_STUDENT_GRADES_FAILED,
  UPDATE_AI_STUDENT_GRADES_STARTED,
  UPDATE_AI_STUDENT_GRADES_FAILED,
  UPDATE_AI_STUDENT_GRADES_SUCCESS,
  FETCH_ALL_CALCULATED_GRADES_STARTED,
  FETCH_ALL_CALCULATED_GRADES_SUCCESS,
  FETCH_ALL_CALCULATED_GRADES_FAILED,
} from './assessmentItems.types';

import formatStudentGrades from './utils/formatStudentGrades';

const fetchAssessmentItemsStarted = (payload) => ({
  type: FETCH_ASSESSMENT_ITEMS_STARTED,
  ...payload,
});

const fetchAssessmentItemsSuccess = (payload) => ({
  type: FETCH_ASSESSMENT_ITEMS_SUCCESS,
  ...payload,
});

const fetchAssessmentItemsError = (payload) => ({
  type: FETCH_ASSESSMENT_ITEMS_FAILED,
  ...payload,
});

const mapAssignmentsStarted = (payload) => ({
  type: MAP_ASSIGNMENTS_STARTED,
  ...payload,
});

const mapAssignmentsSuccess = (payload) => ({
  type: MAP_ASSIGNMENTS_SUCCESS,
  ...payload,
});

const mapAssignmentsFailed = (payload) => ({
  type: MAP_ASSIGNMENTS_FAILED,
  ...payload,
});

const clearMapAssignmentError = () => ({
  type: MAP_ASSIGNMENTS_CLEAR,
});

// Download CSV

const downloadCsvStarted = ({ assessmentItemId }) => ({
  type: DOWNLOAD_CSV_STARTED,
  assessmentItemId,
});

const downloadCsvSuccess = () => ({
  type: DOWNLOAD_CSV_SUCCESS,
});

const downloadCsvError = ({ error }) => ({
  type: DOWNLOAD_CSV_FAILED,
  error,
});

// Export grades

const exportGradesStarted = ({ assessmentItemId }) => ({
  type: EXPORT_GRADES_STARTED,
  assessmentItemId,
});

const exportGradesSuccess = (job) => ({
  type: EXPORT_GRADES_SUCCESS,
  job,
});

const exportGradesError = ({ error, assessmentItemId }) => ({
  type: EXPORT_GRADES_FAILED,
  assessmentItemId,
  error,
});

// get grades in assessments items

const fetchAllCalculatedGradesStarted = ({ assessmentItemId }) => ({
  type: FETCH_ALL_CALCULATED_GRADES_STARTED,
  assessmentItemId,
});

const fetchAllCalculatedGradesSuccess = ({
  assessmentItemId,
  studentGrades,
}) => ({
  type: FETCH_ALL_CALCULATED_GRADES_SUCCESS,
  assessmentItemId,
  studentGrades,
});

const fetchAllCalculatedGradesError = ({ assessmentItemId, error }) => ({
  type: FETCH_ALL_CALCULATED_GRADES_FAILED,
  assessmentItemId,
  error,
});

// get grades for student

const fetchStudentGradesStarted = ({ studentId }) => ({
  type: FETCH_AI_STUDENT_GRADES_STARTED,
  studentId,
});

const fetchStudentGradesSuccess = ({ studentId, studentGrades }) => ({
  type: FETCH_AI_STUDENT_GRADES_SUCCESS,
  studentId,
  studentGrades,
});

const fetchStudentGradesError = ({ studentId, error }) => ({
  type: FETCH_AI_STUDENT_GRADES_FAILED,
  studentId,
  error,
});

// update grades for student
const updateStudentGradesStarted = ({ studentId }) => ({
  type: UPDATE_AI_STUDENT_GRADES_STARTED,
  studentId,
});

const updateStudentGradesSuccess = ({ studentId, studentGrades }) => ({
  type: UPDATE_AI_STUDENT_GRADES_SUCCESS,
  studentId,
  studentGrades,
});

const updateStudentGradesError = ({ studentId, error }) => ({
  type: UPDATE_AI_STUDENT_GRADES_FAILED,
  studentId,
  error,
});

export const fetchAssessmentItems =
  ({ spaceId }) =>
  async (dispatch, getState) => {
    const payload = { spaceId };

    dispatch(fetchAssessmentItemsStarted(payload));

    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { assessmentItems } = await getAssessmentItemsBySpaceId({
        spaceId,
        accessToken,
      });
      dispatch(fetchAssessmentItemsSuccess({ ...payload, assessmentItems }));

      Analytics.track({
        event: FETCH_ASSESSMENT_ITEMS,
        totalAssessmentItems: assessmentItems.length,
      });
    } catch (error) {
      dispatch(fetchAssessmentItemsError({ ...payload, error }));
    }
  };

export const startExportEvent =
  ({ objectId, assessmentItemId, gradeExportJobStatus }) =>
  (dispatch) => {
    const job = {
      id: objectId,
      assessmentItemId,
      status: gradeExportJobStatus,
    };

    dispatch({
      type: EXPORT_GRADES_EVENT_START,
      job,
    });
  };

export const endExportEvent = () => async (dispatch, getState) => {
  const spaceId = coreSelectors.getCurrentSpaceId(getState());

  await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
  const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

  try {
    const { assessmentItems } = await getAssessmentItemsBySpaceId({
      spaceId,
      accessToken,
    });
    dispatch(fetchAssessmentItemsSuccess({ assessmentItems }));
  } catch (e) {
    // do nothing
  }
};

export const mapAssignmentsToAssessmentItem =
  ({ assessmentItemId, assignmentMappings, assessmentItemMappings = [] }) =>
  async (dispatch, getState) => {
    const payload = {
      assessmentItemId,
      assignmentMappings,
      assessmentItemMappings,
    };

    dispatch(mapAssignmentsStarted(payload));

    const { id: spaceId } = coreSelectors.getCurrentSpace(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      await postAssignmentsToAssessmentItem({
        assessmentItemId,
        assignmentMappings,
        assessmentItemMappings,
        accessToken,
      });
      const { assessmentItems } = await getAssessmentItemsBySpaceId({
        spaceId,
        accessToken,
      });

      dispatch(fetchAssessmentItemsSuccess({ ...payload, assessmentItems }));

      dispatch(mapAssignmentsSuccess(payload));
    } catch (error) {
      dispatch(mapAssignmentsFailed({ ...payload, error }));
    }
  };

export const clearMapAssignmentsError = () => async (dispatch) => {
  dispatch(clearMapAssignmentError());
};

export const downloadCsv =
  ({
    assessmentItemId,
    overrides,
    overrideOptions,
    includeStudentId = false,
  }) =>
  async (dispatch, getState) => {
    dispatch(downloadCsvStarted({ assessmentItemId }));

    const { id: spaceId } = coreSelectors.getCurrentSpace(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { grades } = await getGradesByAssessmentItemId({
        assessmentItemId,
        accessToken,
      });

      const gradesWithOverrides = mergeOverridesWithGrades({
        grades,
        overrides,
        overrideOptions,
      });

      // generate csv file
      downloadGradeCsv({
        filename: assessmentItemId,
        grades: gradesWithOverrides,
        includeStudentId,
      });

      dispatch(downloadCsvSuccess());
      Analytics.track({
        event: EXPORT_GRADES_AS_CSV,
        assessmentItemId,
        totalGrades: grades.length,
      });
    } catch (error) {
      dispatch(downloadCsvError({ error }));
    }
  };

export const fetchCalculatedGrades =
  ({ assessmentItemId }) =>
  async (dispatch, getState) => {
    dispatch(fetchAllCalculatedGradesStarted({ assessmentItemId }));

    const { id: spaceId } = coreSelectors.getCurrentSpace(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const { grades: studentGrades } = await getGradesByAssessmentItemId({
        assessmentItemId,
        accessToken,
      });

      dispatch(
        fetchAllCalculatedGradesSuccess({ assessmentItemId, studentGrades })
      );
    } catch (error) {
      dispatch(fetchAllCalculatedGradesError({ assessmentItemId, error }));
    }
  };

export const exportGrades =
  ({ assessmentItemId, overrides }) =>
  async (dispatch, getState) => {
    dispatch(exportGradesStarted({ assessmentItemId }));

    const { id: spaceId } = coreSelectors.getCurrentSpace(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);

    try {
      const job = await createExportJob({
        assessmentItemId,
        overrides,
        accessToken,
      });
      dispatch(exportGradesSuccess(job));
    } catch (error) {
      dispatch(exportGradesError({ assessmentItemId, error }));
      throw error;
    }
  };

export const fetchStudentGrades =
  ({ studentId }) =>
  async (dispatch, getState) => {
    dispatch(fetchStudentGradesStarted({ studentId }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);
    const assessmentItems = getAssessmentItems(getState());

    try {
      const results = await Promise.all(
        assessmentItems.map(({ id, mappedAssignments }) => {
          // request with undefined or empty mappedAssignments
          // will result in error status 500
          if (!mappedAssignments || !mappedAssignments.length) {
            return null;
          }

          return getGradesByStudentId({
            assessmentItemId: id,
            studentId,
            accessToken,
          });
        })
      );

      // format to { [assignmentId]: studentGrade }
      // and filterout null value
      const studentGrades = formatStudentGrades(results);

      dispatch(fetchStudentGradesSuccess({ studentId, studentGrades }));
    } catch (error) {
      dispatch(fetchStudentGradesError({ studentId, error }));
    }
  };

export const updateStudentGrades =
  ({ studentId }) =>
  async (dispatch, getState) => {
    dispatch(updateStudentGradesStarted({ studentId }));

    const spaceId = coreSelectors.getCurrentSpaceId(getState());
    await dispatch(coreActions.fetchNewTokenIfNeeded({ spaceId }));
    const accessToken = coreSelectors.getAccessToken(getState(), spaceId);
    const assessmentItems = getAssessmentItems(getState());

    try {
      const results = await Promise.all(
        assessmentItems.map(({ id, mappedAssignments }) => {
          // request with undefined or empty mappedAssignments
          // will result in error status 500
          if (!mappedAssignments || !mappedAssignments.length) {
            return null;
          }

          return getGradesByStudentId({
            assessmentItemId: id,
            studentId,
            accessToken,
          });
        })
      );

      // format to { [assessmentItemId]: studentGrade }
      // and filterout null value
      const studentGrades = formatStudentGrades(results);

      dispatch(updateStudentGradesSuccess({ studentId, studentGrades }));
    } catch (error) {
      dispatch(updateStudentGradesError({ studentId, error }));
    }
  };
